<template>
	<div
		:class="{
			'full-width d-flex justify-center': makeTriggerContentCentered,
		}"
	>
		<template v-if="menuBreakPoint">
			<v-menu
				:content-class="vMenuContentClass"
				:eager="eager"
				:close-on-content-click="closeOnContentClick"
				:bottom="bottom"
				:top="top"
				:left="left"
				:right="right"
				:disabled="disabled"
				:max-height="maxHeight"
				:min-height="minHeight"
				:max-width="maxWidth"
				:min-width="minWidth"
				:nudge-bottom="nudgeBottom"
				:nudge-left="nudgeLeft"
				:nudge-right="nudgeRight"
				:transition="transition"
				:value="computedIsOpen"
				:open-on-hover="openOnHover"
				:open-delay="openDelay"
				:close-on-click="closeOnClick"
				:close-delay="closeDelay"
				@input="setValue"
			>
				<template v-slot:activator="{ on }">
					<slot name="trigger" v-on="on" :on="on"></slot>
				</template>
				<slot style="height: 100vh" name="content" v-bind="{ close, open, isOpen: computedIsOpen, isOpenButOverrideHidden }"></slot>
			</v-menu>
		</template>
		<template v-if="bottomSheetBreakPoint">
			<v-bottom-sheet
				:eager="eager"
				:disabled="disabled"
				:value="computedIsOpen"
				:scrollable="scrollable"
				:persistent="bottomSheetPersistent"
				:content-class="menuAlwaysFullHeight ? 'menu-or-bottom-sheet-bottom-sheet-content-full-height' : 'menu-or-bottom-sheet-bottom-sheet-content'"
				@input="setValue"
			>
				<template v-slot:activator="{ on }">
					<slot name="trigger" v-on="on" :on="on"></slot>
				</template>
				<slot style="height: 100vh" name="content" v-bind="{ close, open, isOpen: computedIsOpen, isOpenButOverrideHidden }"></slot>
			</v-bottom-sheet>
		</template>
	</div>
</template>

<script>
import Vue from 'vue';

export default Vue.extend({
	name: 'MenuOrBottomSheet',
	props: {
		// shared props
		value: { type: Boolean, default: false },
		overrideHidden: { type: Boolean, default: false },
		eager: { type: Boolean, default: false },
		disabled: { type: Boolean, default: false },
		makeTriggerContentCentered: { type: Boolean, default: false },
		// menu props
		menuAlwaysFullHeight: { type: Boolean, default: false },
		maxWidth: { type: [String, Number], default: 320 },
		minWidth: { type: [String, Number], default: 300 },
		maxHeight: { type: [String, Number], default: 600 },
		minHeight: { type: [String, Number], default: 600 },
		transition: { type: String, default: 'fade-transition' },
		bottom: { type: Boolean, default: true },
		top: { type: Boolean, default: false },
		left: { type: Boolean, default: false },
		right: { type: Boolean, default: false },
		nudgeBottom: { type: Number, default: 40 },
		nudgeLeft: { type: Number, default: 0 },
		nudgeRight: { type: Number, default: 0 },
		closeOnContentClick: { type: Boolean, default: false },
		closeOnClick: { type: Boolean, default: true },
		openOnHover: { type: Boolean, default: false },
		closeDelay: { type: Number, default: 0 },
		openDelay: { type: Number, default: 0 },
		// bottom sheet props
		scrollable: { type: Boolean, default: false },
		bottomSheetPersistent: { type: Boolean, default: false },
		bottomSheetOnSmall: { type: Boolean, default: false },
		bottomSheetOnMedium: { type: Boolean, default: false },
		isBottomSheet: { type: Boolean, default: false },
		/** Appended to v-menu content-class (space-separated). Use for portaled menu styling hooks. */
		extraMenuContentClass: { type: String, default: '' },
	},
	data() {
		return {
			isOpen: this.value,
		};
	},
	watch: {
		value(val) {
			this.setValue(val);
		},
	},
	computed: {
		menuBreakPoint() {
			let breakPoint = this.$vuetify.breakpoint.smAndUp;

			if (this.bottomSheetOnSmall && !this.isBottomSheet) {
				breakPoint = this.$vuetify.breakpoint.mdAndUp;
			}

			if (this.bottomSheetOnMedium && !this.isBottomSheet) {
				breakPoint = this.$vuetify.breakpoint.lgAndUp;
			}

			if (this.isBottomSheet) {
				breakPoint = null;
			}

			return breakPoint;
		},
		bottomSheetBreakPoint() {
			let breakPoint = this.$vuetify.breakpoint.xsOnly;

			if (this.bottomSheetOnSmall && !this.isBottomSheet) {
				breakPoint = this.$vuetify.breakpoint.smAndDown;
			}

			if (this.bottomSheetOnMedium && !this.isBottomSheet) {
				breakPoint = this.$vuetify.breakpoint.mdAndDown;
			}

			if (this.isBottomSheet) {
				breakPoint = this.$vuetify.breakpoint.smAndUp;
			}

			return breakPoint;
		},
		computedIsOpen() {
			return !!(
				this.isOpen &&
				!this.overrideHidden
			);
		},
		isOpenButOverrideHidden() {
			return !!(
				this.isOpen &&
				this.overrideHidden
			);
		},
		vMenuContentClass () {
			const base = this.menuAlwaysFullHeight
				? 'menu-or-bottom-sheet-menu-content-full-height'
				: 'menu-or-bottom-sheet-menu-content';
			const extra = (this.extraMenuContentClass || '').trim();
			return extra ? `${base} ${extra}` : base;
		},
	},
	methods: {
		close() {
			this.setValue(false);
		},
		open() {
			this.setValue(true);
		},
		setValue(val) {
			if (val !== this.isOpen) {
				this.$emit('input', val);
			}
			this.isOpen = val;
		},
	},
});
</script>

<style lang="scss" scoped>
.menu-or-bottom-sheet-menu-content {
	border-radius: var(--global-border-radius) !important;
}

.menu-or-bottom-sheet-menu-content-full-height {
	border-radius: var(--global-border-radius) !important;
	height: 100vh;
}

.menu-or-bottom-sheet-bottom-sheet-content {
	border-top-right-radius: var(--global-border-radius) !important;
	border-top-left-radius: var(--global-border-radius) !important;
}

.menu-or-bottom-sheet-bottom-sheet-content-full-height {
	border-top-right-radius: var(--global-border-radius) !important;
	border-top-left-radius: var(--global-border-radius) !important;
	height: 100vh;
}
</style>
